import { useEffect, useState } from "react";
import { Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { FullscreenTakeover } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { ProfileCreationPreview } from "~/pages/Compliance/documents/ProfileCreationPreview";
import { ProfileCreationModal } from "../../common/AutoProfileCreationCard/ProfileCreationModal";
import CreateEntityForm from "./CreateEntityForm";
import CreateIndividualForm from "./CreateIndividualForm";
import SelectCreateMethodForm from "./SelectCreateMethodForm";
import SelectOwnerOrControllerForm from "./SelectOwnerOrControllerForm";
import { isEntityClientOnboardingCase } from "./utils/isEntityClientOnboardingCase";

interface Props {
  caseId: number;
  investingProfileId: number | undefined;
}

const CreateOwnersAndControllersFullscreenTakeover = ({
  caseId,
  investingProfileId,
}: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { caseType } = useCurrentCaseContext();
  const isOnboardingCase = isEntityClientOnboardingCase(caseType);

  const [parentProfileId, setParentProfileId] = useState(investingProfileId);
  const [positionId, setPositionId] = useState<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const parentProfileIdParam = searchParams.get("parentProfileId");
    const positionIdParam = searchParams.get("positionId");

    if (parentProfileIdParam) {
      setParentProfileId(parseInt(parentProfileIdParam, 10));
    }
    if (positionIdParam) {
      setPositionId(positionIdParam);
    }
  }, [location.search]);

  const onExit = () => {
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles`);
  };

  const methodBackSearch = positionId ? `?positionId=${positionId}` : "";

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        onExit={onExit}
        title={positionId ? "Payment beneficiary" : "Create new owner or controller"}
      />
      <Switch>
        <Route path={`${match.path}/:profileType/extraction/preview`} exact>
          <ProfileCreationPreview
            parentProfileId={parentProfileId}
            onCancel={onExit}
            onComplete={onExit}
          />
        </Route>
        <Route path={`${match.path}/individual/method`} exact>
          <SelectCreateMethodForm />
        </Route>
        <Route path={`${match.path}/entity/method`} exact>
          <SelectCreateMethodForm />
        </Route>
        <Route path={`${match.path}/individual`}>
          <FullscreenTakeover.Body>
            <CreateIndividualForm
              caseId={caseId}
              parentProfileId={parentProfileId}
              inAiExtractionMode
              isOnboardingCase={isOnboardingCase}
              positionId={positionId}
              backNavigationLabel={isOnboardingCase ? "Select create method" : undefined}
              navigateOnCancel={() => {
                if (isOnboardingCase) {
                  history.push(
                    `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers/individual/method${methodBackSearch}`,
                  );
                  return;
                }
                history.push(
                  `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers${methodBackSearch}`,
                );
              }}
              navigateOnCompletion={() => {
                history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles`);
              }}
            />
          </FullscreenTakeover.Body>
          <Route path={`${match.path}/individual/extraction`} exact>
            <ProfileCreationModal />
          </Route>
        </Route>
        <Route path={`${match.path}/entity`}>
          <FullscreenTakeover.Body>
            <CreateEntityForm
              caseId={caseId}
              parentProfileId={parentProfileId}
              positionId={positionId}
              inAiExtractionMode
              isOnboardingCase={isOnboardingCase}
              backNavigationLabel={isOnboardingCase ? "Create method" : undefined}
              navigateOnCancel={() => {
                if (isOnboardingCase) {
                  history.push(
                    `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers/entity/method${methodBackSearch}`,
                  );
                  return;
                }
                history.push(
                  `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers${methodBackSearch}`,
                );
              }}
              navigateOnCompletion={() => {
                history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles`);
              }}
            />
          </FullscreenTakeover.Body>
          <Route path={`${match.path}/entity/extraction`} exact>
            <ProfileCreationModal />
          </Route>
        </Route>
        <Route path={`${match.path}`} exact>
          <SelectOwnerOrControllerForm caseId={caseId} />
        </Route>
      </Switch>
    </FullscreenTakeover>
  );
};

export default CreateOwnersAndControllersFullscreenTakeover;
